import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { supabase } from '@/lib/supabase'
import { formatCST } from '@/lib/time'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { parseAnimeIdInput } from '@/lib/subtitle-translate'
import { useDebounced, useIsMobile } from '@/lib/hooks'

export const Route = createFileRoute('/_authed/rss-notifications')({
  component: RssNotificationsPage,
})

interface RssRule {
  id: number
  anime_id: number
  anime_title: string
  enabled: boolean
  label: string
  include_keywords: string[]
  exclude_keywords: string[]
  preferred_authors: string[]
  preferred_quality: string | null
  episode_offset: number
  last_matched_at: string | null
  created_at: string
  updated_at: string
}

interface RssNotification {
  id: number
  status: 'sent' | 'failed' | 'skipped'
  error: string | null
  sent_at: string | null
  created_at: string
  parsed_episode_number: number | null
  parsed_quality: string | null
  confidence: number
  reasons: string[]
  rss_title: string
  rss_link: string | null
  pub_date: string | null
  author: string | null
  category: string | null
  rule_id: number
  rule_label: string
  anime_id: number
  anime_title: string
}

interface AnimeOption {
  id: number
  title: string
}

interface RpcListResult<T> {
  ok: boolean
  rows?: T[]
  total?: number
  error?: string
}

interface RpcResult {
  ok: boolean
  error?: string
}

interface RuleFormValues {
  anime_id?: number
  enabled?: boolean
  label?: string
  include_keywords?: string
  exclude_keywords?: string
  preferred_authors?: string
  preferred_quality?: string
  episode_offset?: number
}

const PAGE_SIZE = 50

function splitWords(value: string | undefined): string[] {
  return (value ?? '')
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean)
}

function joinWords(value: string[]): string {
  return value.join('\n')
}

async function searchAnimeOptions(keyword: string): Promise<AnimeOption[]> {
  const directAnimeId = parseAnimeIdInput(keyword)
  if (directAnimeId != null) {
    const { data, error } = await supabase
      .from('animes')
      .select('id, title')
      .eq('id', directAnimeId)
      .maybeSingle()
    if (error) throw error
    return data ? [data] : []
  }

  const { data, error } = await supabase
    .from('animes')
    .select('id, title')
    .ilike('search_title', `%${keyword}%`)
    .order('view_count', { ascending: false, nullsFirst: false })
    .limit(20)
  if (error) throw error
  return data
}

function statusMeta(status: RssNotification['status']) {
  switch (status) {
    case 'sent':
      return { label: '已发送', color: 'green' }
    case 'failed':
      return { label: '失败', color: 'red' }
    case 'skipped':
    default:
      return { label: '跳过', color: 'default' }
  }
}

function confidenceColor(value: number): string {
  if (value >= 80) return 'green'
  if (value >= 50) return 'orange'
  return 'default'
}

function ensureOk(
  result: RpcResult | null | undefined,
  fallback: string,
): void {
  if (!result?.ok) throw new Error(result?.error ?? fallback)
}

function KeywordTags({ values }: { values: string[] }) {
  if (values.length === 0)
    return <Typography.Text type="secondary">-</Typography.Text>
  return (
    <Space size={4} wrap>
      {values.map((item) => (
        <Tag key={item}>{item}</Tag>
      ))}
    </Space>
  )
}

function RssNotificationsPage() {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const isMobile = useIsMobile()
  const adminRpc = supabase.schema('admin' as never) as any
  const [ruleOpen, setRuleOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<RssRule | null>(null)
  const [selectedAnimeOption, setSelectedAnimeOption] =
    useState<AnimeOption | null>(null)
  const [animeSearchInput, setAnimeSearchInput] = useState('')
  const [page, setPage] = useState(1)
  const [form] = Form.useForm<RuleFormValues>()
  const debouncedAnimeSearch = useDebounced(animeSearchInput.trim(), 250)

  const rulesQuery = useQuery({
    queryKey: ['rss_watch_rules'],
    queryFn: async () => {
      const { data, error } = await adminRpc.rpc('list_rss_watch_rules')
      if (error) throw error
      const result = data as RpcListResult<RssRule>
      ensureOk(result, 'list_rss_watch_rules failed')
      return result.rows ?? []
    },
  })

  const notificationsQuery = useQuery({
    queryKey: ['rss_notifications', page],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await adminRpc.rpc('list_rss_notifications', {
        p_limit: PAGE_SIZE,
        p_offset: (page - 1) * PAGE_SIZE,
      })
      if (error) throw error
      const result = data as RpcListResult<RssNotification>
      ensureOk(result, 'list_rss_notifications failed')
      return {
        rows: result.rows ?? [],
        total: result.total ?? 0,
      }
    },
  })

  const animeOptionsQuery = useQuery({
    queryKey: ['rss_rule_anime_search', debouncedAnimeSearch],
    enabled: debouncedAnimeSearch.length >= 1,
    queryFn: () => searchAnimeOptions(debouncedAnimeSearch),
    staleTime: 60_000,
  })

  const animeSelectOptions = useMemo(() => {
    const list = animeOptionsQuery.data ?? []
    if (
      selectedAnimeOption &&
      !list.some((anime) => anime.id === selectedAnimeOption.id)
    ) {
      return [selectedAnimeOption, ...list]
    }
    return list
  }, [animeOptionsQuery.data, selectedAnimeOption])

  const saveRule = useMutation({
    mutationFn: async (values: RuleFormValues) => {
      const { data, error } = await adminRpc.rpc('upsert_rss_watch_rule', {
        p_rule: {
          id: editingRule?.id,
          anime_id: values.anime_id,
          enabled: values.enabled !== false,
          label: values.label,
          include_keywords: splitWords(values.include_keywords),
          exclude_keywords: splitWords(values.exclude_keywords),
          preferred_authors: splitWords(values.preferred_authors),
          preferred_quality: values.preferred_quality?.trim() || null,
          episode_offset: values.episode_offset ?? 0,
        },
      })
      if (error) throw error
      ensureOk(data as RpcResult, 'upsert_rss_watch_rule failed')
    },
    onSuccess: () => {
      message.success('规则已保存')
      setRuleOpen(false)
      setEditingRule(null)
      void qc.invalidateQueries({ queryKey: ['rss_watch_rules'] })
    },
    onError: (error: Error) => message.error(error.message || '保存失败'),
  })

  const toggleRule = useMutation({
    mutationFn: async (vars: { id: number; enabled: boolean }) => {
      const { data, error } = await adminRpc.rpc('set_rss_watch_rule_enabled', {
        p_rule_id: vars.id,
        p_enabled: vars.enabled,
      })
      if (error) throw error
      ensureOk(data as RpcResult, 'set_rss_watch_rule_enabled failed')
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['rss_watch_rules'] })
    },
    onError: (error: Error) => message.error(error.message || '切换失败'),
  })

  function openNewRule() {
    setEditingRule(null)
    setSelectedAnimeOption(null)
    setAnimeSearchInput('')
    form.setFieldsValue({
      anime_id: undefined,
      enabled: true,
      label: '',
      include_keywords: '',
      exclude_keywords: '',
      preferred_authors: '',
      preferred_quality: '',
      episode_offset: 0,
    })
    setRuleOpen(true)
  }

  function openEditRule(rule: RssRule) {
    setEditingRule(rule)
    setSelectedAnimeOption({ id: rule.anime_id, title: rule.anime_title })
    setAnimeSearchInput('')
    form.setFieldsValue({
      anime_id: rule.anime_id,
      enabled: rule.enabled,
      label: rule.label,
      include_keywords: joinWords(rule.include_keywords),
      exclude_keywords: joinWords(rule.exclude_keywords),
      preferred_authors: joinWords(rule.preferred_authors),
      preferred_quality: rule.preferred_quality ?? '',
      episode_offset: rule.episode_offset,
    })
    setRuleOpen(true)
  }

  const ruleColumns: ColumnsType<RssRule> = useMemo(
    () => [
      {
        title: '规则',
        render: (_: unknown, row) => (
          <Space direction="vertical" size={2}>
            <Space size={6} wrap>
              <Typography.Text strong>{row.label}</Typography.Text>
              <Tag color={row.enabled ? 'green' : 'default'}>
                {row.enabled ? '启用' : '停用'}
              </Tag>
            </Space>
            <Typography.Text type="secondary">
              #{row.anime_id} · {row.anime_title}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '包含关键词',
        dataIndex: 'include_keywords',
        render: (values: string[]) => <KeywordTags values={values} />,
      },
      {
        title: '排除 / 字幕组',
        width: 220,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={4}>
            <KeywordTags values={row.exclude_keywords} />
            <KeywordTags values={row.preferred_authors} />
          </Space>
        ),
      },
      {
        title: '偏好',
        width: 160,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={2}>
            <Typography.Text>{row.preferred_quality ?? '-'}</Typography.Text>
            <Typography.Text type="secondary">
              集数偏移 {row.episode_offset}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '最近匹配',
        width: 150,
        render: (_: unknown, row) => (
          <Typography.Text type="secondary">
            {row.last_matched_at
              ? formatCST(row.last_matched_at, 'MM-DD HH:mm')
              : '-'}
          </Typography.Text>
        ),
      },
      {
        title: '操作',
        width: 150,
        render: (_: unknown, row) => (
          <Space size={8}>
            <Tooltip title="编辑">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditRule(row)}
              />
            </Tooltip>
            <Switch
              size="small"
              checked={row.enabled}
              loading={
                toggleRule.isPending && toggleRule.variables.id === row.id
              }
              onChange={(enabled) => toggleRule.mutate({ id: row.id, enabled })}
            />
          </Space>
        ),
      },
    ],
    [toggleRule],
  )

  const notificationColumns: ColumnsType<RssNotification> = useMemo(
    () => [
      {
        title: 'RSS 条目',
        render: (_: unknown, row) => (
          <Space direction="vertical" size={2}>
            {row.rss_link ? (
              <Typography.Link href={row.rss_link} target="_blank">
                {row.rss_title}
              </Typography.Link>
            ) : (
              <Typography.Text>{row.rss_title}</Typography.Text>
            )}
            <Typography.Text type="secondary">
              {row.author ?? '-'} · {row.category ?? '-'}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '番剧 / 规则',
        width: 220,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={2}>
            <Typography.Text>{row.anime_title}</Typography.Text>
            <Typography.Text type="secondary">{row.rule_label}</Typography.Text>
          </Space>
        ),
      },
      {
        title: '识别',
        width: 170,
        render: (_: unknown, row) => (
          <Space size={4} wrap>
            {row.parsed_episode_number == null ? null : (
              <Tag color="blue">第 {row.parsed_episode_number} 集</Tag>
            )}
            {row.parsed_quality ? <Tag>{row.parsed_quality}</Tag> : null}
            <Tag color={confidenceColor(row.confidence)}>{row.confidence}%</Tag>
          </Space>
        ),
      },
      {
        title: '状态',
        width: 120,
        render: (_: unknown, row) => {
          const meta = statusMeta(row.status)
          return (
            <Space direction="vertical" size={2}>
              <Tag color={meta.color}>{meta.label}</Tag>
              {row.error ? (
                <Tooltip title={row.error}>
                  <Typography.Text
                    type="danger"
                    ellipsis
                    style={{ maxWidth: 90 }}
                  >
                    {row.error}
                  </Typography.Text>
                </Tooltip>
              ) : null}
            </Space>
          )
        },
      },
      {
        title: '时间',
        width: 170,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={2}>
            <Typography.Text>
              {row.sent_at ? formatCST(row.sent_at, 'MM-DD HH:mm') : '-'}
            </Typography.Text>
            <Typography.Text type="secondary">
              入库 {formatCST(row.created_at, 'MM-DD HH:mm')}
            </Typography.Text>
          </Space>
        ),
      },
    ],
    [],
  )

  const rules = rulesQuery.data ?? []
  const notifications = notificationsQuery.data?.rows ?? []
  const notificationTotal = notificationsQuery.data?.total ?? 0

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card
        title="RSS 监控规则"
        extra={
          <Space size={8} wrap>
            <Tooltip title={isMobile ? '刷新' : undefined}>
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                onClick={() => void rulesQuery.refetch()}
                loading={rulesQuery.isFetching}
              >
                {isMobile ? null : '刷新'}
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openNewRule}
            >
              {isMobile ? '新建' : '新建规则'}
            </Button>
          </Space>
        }
      >
        <div className="admin-table-wrap">
          <Table<RssRule>
            rowKey="id"
            size="small"
            columns={ruleColumns}
            dataSource={rules}
            loading={rulesQuery.isLoading}
            pagination={{
              pageSize: 20,
              showSizeChanger: false,
              showTotal: (total) => `共 ${total} 条`,
            }}
          />
        </div>

        <AdminMobileList
          rows={rules}
          rowKey={(row) => row.id}
          loading={rulesQuery.isLoading}
          renderItem={(row) => (
            <article
              className="admin-mobile-record"
              role="button"
              tabIndex={0}
              onClick={() => openEditRule(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') openEditRule(row)
              }}
            >
              <div className="admin-mobile-record__head">
                <div className="admin-mobile-record__title">
                  {row.label}
                  <div className="admin-mobile-record__meta">
                    #{row.anime_id} · {row.anime_title}
                  </div>
                </div>
                <Tag color={row.enabled ? 'green' : 'default'}>
                  {row.enabled ? '启用' : '停用'}
                </Tag>
              </div>
              <div className="admin-mobile-record__badges">
                <KeywordTags values={row.include_keywords} />
              </div>
              <div className="admin-mobile-record__meta">
                {row.preferred_authors.length > 0 ? (
                  <span>字幕组 {row.preferred_authors.join(' / ')}</span>
                ) : null}
                {row.preferred_quality ? (
                  <span>{row.preferred_quality}</span>
                ) : null}
                <span>
                  最近匹配{' '}
                  {row.last_matched_at
                    ? formatCST(row.last_matched_at, 'MM-DD HH:mm')
                    : '-'}
                </span>
                <span>偏移 {row.episode_offset}</span>
              </div>
              {/* 卡片点开即编辑；底部只留启停开关 */}
              <AdminRecordActions
                actions={[]}
                leading={
                  <>
                    <Switch
                      size="small"
                      checked={row.enabled}
                      aria-label="启用规则"
                      loading={
                        toggleRule.isPending &&
                        toggleRule.variables.id === row.id
                      }
                      onChange={(enabled) =>
                        toggleRule.mutate({ id: row.id, enabled })
                      }
                    />
                    {row.enabled ? '已启用' : '已停用'}
                  </>
                }
              />
            </article>
          )}
        />
      </Card>

      <Card
        title="Telegram 通知历史"
        extra={
          <Tooltip title={isMobile ? '刷新' : undefined}>
            <Button
              icon={<ReloadOutlined />}
              aria-label="刷新"
              onClick={() => void notificationsQuery.refetch()}
              loading={notificationsQuery.isFetching}
            >
              {isMobile ? null : '刷新'}
            </Button>
          </Tooltip>
        }
      >
        <div className="admin-table-wrap">
          <Table<RssNotification>
            rowKey="id"
            size="small"
            columns={notificationColumns}
            dataSource={notifications}
            loading={notificationsQuery.isLoading}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: notificationTotal,
              showSizeChanger: false,
              showTotal: (total) => `共 ${total} 条`,
              onChange: setPage,
            }}
          />
        </div>

        <AdminMobileList
          rows={notifications}
          rowKey={(row) => row.id}
          loading={notificationsQuery.isLoading}
          page={page}
          pageSize={PAGE_SIZE}
          total={notificationTotal}
          onPageChange={setPage}
          renderItem={(row) => {
            const meta = statusMeta(row.status)
            return (
              <article className="admin-mobile-record">
                <div className="admin-mobile-record__head">
                  <div className="admin-mobile-record__title rss-mobile-title">
                    {row.rss_link ? (
                      <Typography.Link
                        href={row.rss_link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {row.rss_title}
                      </Typography.Link>
                    ) : (
                      row.rss_title
                    )}
                    <div className="admin-mobile-record__meta">
                      {row.anime_title} · {row.rule_label}
                    </div>
                  </div>
                  <Tag color={meta.color}>{meta.label}</Tag>
                </div>
                <div className="admin-mobile-record__badges">
                  {row.parsed_episode_number == null ? null : (
                    <Tag color="blue">第 {row.parsed_episode_number} 集</Tag>
                  )}
                  {row.parsed_quality ? <Tag>{row.parsed_quality}</Tag> : null}
                  <Tag color={confidenceColor(row.confidence)}>
                    {row.confidence}%
                  </Tag>
                </div>
                {row.error ? (
                  <div className="admin-mobile-record__body">{row.error}</div>
                ) : null}
                <div className="admin-mobile-record__meta">
                  <span>
                    {row.sent_at
                      ? `发送 ${formatCST(row.sent_at, 'MM-DD HH:mm')}`
                      : `入库 ${formatCST(row.created_at, 'MM-DD HH:mm')}`}
                  </span>
                  <span>{row.author ?? '-'}</span>
                </div>
              </article>
            )
          }}
        />
      </Card>

      <Drawer
        title={editingRule ? '编辑 RSS 规则' : '新建 RSS 规则'}
        width={isMobile ? '100%' : 520}
        open={ruleOpen}
        onClose={() => setRuleOpen(false)}
        destroyOnHidden
        extra={
          <Button
            type="primary"
            loading={saveRule.isPending}
            onClick={() => form.submit()}
          >
            保存
          </Button>
        }
      >
        <Form<RuleFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => saveRule.mutate(values)}
        >
          <Form.Item name="enabled" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
          <Form.Item
            name="anime_id"
            label="绑定番剧"
            rules={[{ required: true, message: '请选择番剧' }]}
          >
            <Select<number>
              allowClear
              showSearch
              placeholder="输入番剧名或 ID 搜索"
              filterOption={false}
              onSearch={setAnimeSearchInput}
              onClear={() => {
                setSelectedAnimeOption(null)
                setAnimeSearchInput('')
              }}
              onChange={(value) => {
                const next =
                  animeSelectOptions.find((anime) => anime.id === value) ?? null
                setSelectedAnimeOption(next)
                if (next && !form.getFieldValue('label')?.trim()) {
                  form.setFieldValue('label', next.title)
                }
              }}
              loading={animeOptionsQuery.isFetching}
              notFoundContent={
                debouncedAnimeSearch
                  ? animeOptionsQuery.isFetching
                    ? '搜索中…'
                    : '无匹配'
                  : '输入番剧名或 ID'
              }
              optionLabelProp="label"
              options={animeSelectOptions.map((anime) => ({
                value: anime.id,
                label: `${anime.title} (#${anime.id})`,
              }))}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="label"
            label="规则名"
            rules={[
              { required: true, whitespace: true, message: '请输入规则名' },
            ]}
          >
            <Input placeholder="例：葬送的芙莉莲 桜都字幕组" />
          </Form.Item>
          <Form.Item
            name="include_keywords"
            label="包含关键词"
            rules={[
              {
                required: true,
                whitespace: true,
                message: '至少填写一个关键词',
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder={'一行一个关键词\n可用逗号分隔'}
            />
          </Form.Item>
          <Form.Item name="exclude_keywords" label="排除关键词">
            <Input.TextArea rows={3} placeholder={'合集\n繁体\n720p'} />
          </Form.Item>
          <Form.Item name="preferred_authors" label="偏好字幕组">
            <Input.TextArea rows={3} placeholder={'桜都字幕组\n喵萌奶茶屋'} />
          </Form.Item>
          <Space size={12} style={{ width: '100%' }} align="start">
            <Form.Item name="preferred_quality" label="偏好画质">
              <Input placeholder="1080p" />
            </Form.Item>
            <Form.Item name="episode_offset" label="集数偏移">
              <InputNumber precision={0} style={{ width: 140 }} />
            </Form.Item>
          </Space>
        </Form>
      </Drawer>
    </Space>
  )
}
